/**
 * Enforcement Auto-Insertion Engine
 *
 * When a playbook template is loaded onto the canvas, this engine automatically
 * injects governance enforcement nodes at the correct positions in the DAG.
 *
 * This is the core differentiator: "enforcement is not bolted on — it's woven in."
 *
 * Insertion Rules:
 * 1. DPG_INBOUND  → After every connector_task / threat_intel_lookup (scans incoming data)
 * 2. DPG_OUTBOUND → Before every llm_task (scans data leaving to LLM)
 * 3. TOKEN_VAULT  → After DPG_INBOUND when tokenization action is configured
 * 4. POLICY_GATE  → Before every action node (quarantine, block, isolate, etc.)
 * 5. AUDIT_LOG    → At the end of every terminal path (before close/complete)
 * 6. AUTONOMY_GATE → Before the first action node (checks agent trust level)
 * 7. DPG_INTERNODE → Between EVERY pair of connected nodes for zero-trust data flow tracking (lightweight classification only)
 */

import { getNodePorts } from '../data/soarNodes';

// ─── Port resolution ──────────────────────────────────────────────────

/**
 * The ports an inserted enforcement node actually declares.
 *
 * <p>These were hardcoded as `fromPort: 'output'` and `toPort: 'input'`, and
 * both were wrong: every enforcement node in the registry declares
 * `in: [default]` and `out: [default (Allowed), blocked (Blocked)]`. Nothing
 * in the product produces or consumes 'output'/'input' as a structural
 * handle.
 *
 * It survived because the trunk walk has a fallback — with exactly one
 * outgoing edge it follows the only non-auxiliary one whatever it is called.
 * Draw a second edge out of an inserted scan and the fallback stops applying:
 * the walk prefers the `default` handle, takes the new edge, and every node
 * downstream of the enforcement node disappears from the canvas.
 *
 * Asking the registry rather than restating a literal also means a node type
 * that changes its ports changes them in one place. That is only possible now
 * that the two auto-inserted types are actually registered — they were not,
 * which is why this was hardcoded in the first place.
 */
function enforcementInPort(nodeType) {
  const ports = getNodePorts(nodeType);
  const first = ports && Array.isArray(ports.in) ? ports.in[0] : null;
  const name = first && (typeof first === 'string' ? first : first.name);
  return name || 'default';
}

function enforcementOutPort(nodeType) {
  // The FIRST declared out-port is the pass-through: for every enforcement
  // node that is `default` ("Allowed"), with `blocked` as the governance
  // branch. Splicing must continue the trunk, never divert it into `blocked`.
  const ports = getNodePorts(nodeType);
  const first = ports && Array.isArray(ports.out) ? ports.out[0] : null;
  const name = first && (typeof first === 'string' ? first : first.name);
  return name || 'default';
}

// ─── Node type classification ─────────────────────────────────────────

const CONNECTOR_TYPES = new Set([
  'connector_task', 'threat_intel_lookup', 'http_call',
  'siem_alert', 'email_trigger',
]);

const LLM_TYPES = new Set([
  'llm_task', 'invoke_skill', 'agent_skill',
]);

const ACTION_TYPES = new Set([
  'quarantine_email', 'block_sender', 'endpoint_isolation',
  'credential_reset', 'containment', 'incident_create',
  'notification_task', 'send_notification',
]);

const TERMINAL_TYPES = new Set([
  'system_task', 'close_workflow', 'complete',
]);

// G459 — Flow-sentinel node types that MUST NOT have outbound edges per
// workflow semantics, and therefore MUST NOT have any enforcement scans
// inserted AFTER them. A `terminate` or `end` node is the canonical
// "stop here" sentinel in a Conductor / Agentlets workflow; inserting a
// scan between a terminate and a downstream node creates two bugs:
//   1. The downstream node becomes structurally unreachable (terminate
//      semantically halts the flow; any node "after" it is dead code).
//   2. The IR walker correctly refuses to traverse past terminate
//      sentinels, so the inserted scan + its downstream chain get
//      silently dropped — W133 surfaces them as Disconnected.
//
// Repro before this fix: Native Insurance Claim v37 had a malformed
// `n_terminate_ref → n_terminate_ref_2` edge in the source payload
// (likely a human-authoring bug or a buggy Conductor round-trip). The
// Phase-5 DPG injector then spliced a scan in between, producing
// `n_terminate_ref → dpg_internode_scan → n_terminate_ref_2`, and the
// scan + second terminate dangled as orphans.
//
// The injector is defense-in-depth: even if a malformed edge slips
// through validation, we refuse to extend the chain. The malformed
// edge stays in the payload for W133 / the workflow editor to flag.
const FLOW_SENTINEL_TYPES = new Set([
  'terminate', 'end',
]);

const ENFORCEMENT_TYPES = new Set([
  'dpg_scan', 'dpg_scan_inbound', 'dpg_scan_outbound',
  'dpg_internode_scan',
  'token_vault', 'vault_resolve', 'policy_enforcement_gate',
  'policy_enforcement', 'policy_audit', 'compliance_scoring',
  'agent_autonomy_gate', 'data_classification',
  'policy_rule_evaluation', 'compliance_score',
]);

// General-purpose node types that users or the AI builder may place as workflow logic.
// These are NOT platform enforcement nodes and should not trigger "already governed".
// approval_gate is a user-placed workflow node (e.g. "Underwriter Approval Gate").
const USER_WORKFLOW_TYPES = new Set([
  'approval_gate',
]);

/**
 * Platform enforcement node types — the specific DPG/governance nodes that
 * autoInsertEnforcementNodes() creates.  This is a STRICT subset of
 * ENFORCEMENT_TYPES. Does NOT include general-purpose nodes like approval_gate
 * that users or the AI builder can place.
 *
 * Used by isWorkflowProtected() and the already-governed check in
 * autoInsertEnforcementNodes() to determine whether enforcement is present.
 */
const PLATFORM_ENFORCEMENT_TYPES = new Set([
  'dpg_scan', 'dpg_scan_inbound', 'dpg_scan_outbound',
  'dpg_internode_scan',
  'token_vault', 'vault_resolve',
  'policy_enforcement_gate', 'policy_enforcement',
  'policy_audit', 'compliance_scoring', 'compliance_score',
  'agent_autonomy_gate', 'policy_rule_evaluation',
]);

// ─── Enforcement node templates ───────────────────────────────────────

const createDpgInboundNode = (afterNodeId, index) => ({
  id: `enf_dpg_in_${afterNodeId}_${index}`,
  type: 'dpg_scan_inbound',
  label: 'DPG Scan Inbound',
  config: {
    scanTarget: 'connector_response',
    actions: ['TOKENIZE', 'REDACT', 'MASK'],
    patterns: ['SSN', 'CREDIT_CARD', 'EMAIL', 'PHONE', 'DOB', 'ADDRESS', 'PASSPORT'],
    sensitivity: 'HIGH',
    failAction: 'BLOCK',
    description: 'Auto-inserted: scans connector response for PII/PHI before workflow processing',
  },
  _enforcement: true,
  _insertedAfter: afterNodeId,
  _autoInserted: true,
});

const createDpgOutboundNode = (beforeNodeId, index) => ({
  id: `enf_dpg_out_${beforeNodeId}_${index}`,
  type: 'dpg_scan_outbound',
  label: 'DPG Scan Outbound',
  config: {
    scanTarget: 'llm_prompt',
    actions: ['TOKENIZE', 'REDACT'],
    patterns: ['SSN', 'CREDIT_CARD', 'EMAIL', 'PHONE', 'DOB', 'ADDRESS'],
    sensitivity: 'HIGH',
    failAction: 'BLOCK',
    description: 'Auto-inserted: scans data before sending to LLM to prevent PII leakage',
  },
  _enforcement: true,
  _insertedBefore: beforeNodeId,
  _autoInserted: true,
});

const createTokenVaultNode = (afterDpgId, index) => ({
  id: `enf_vault_${afterDpgId}_${index}`,
  type: 'token_vault',
  label: 'Token Vault',
  config: {
    operation: 'tokenize',
    encryption: 'AES-256-GCM',
    keyManagement: 'envelope',
    vaultConnectorId: null, // populated at runtime from installed secrets-vault connectors
    ttlHours: 24,
    scope: 'workflow-execution',
    description: 'Auto-inserted: tokenizes detected PII with envelope encryption (KMS-wrapped DEK)',
  },
  _enforcement: true,
  _insertedAfter: afterDpgId,
  _autoInserted: true,
});

const createPolicyGateNode = (beforeNodeId, index) => ({
  id: `enf_policy_${beforeNodeId}_${index}`,
  type: 'policy_enforcement_gate',
  label: 'Policy Gate',
  config: {
    evaluationMode: 'strict',
    requiredClassification: 'CONFIDENTIAL',
    checkMandatoryFields: true,
    checkProhibitedActions: true,
    checkTemporalWindow: true,
    failAction: 'BLOCK',
    description: 'Auto-inserted: enforces policy rules before executing security action',
  },
  _enforcement: true,
  _insertedBefore: beforeNodeId,
  _autoInserted: true,
});

const createAuditNode = (beforeTerminalId, index) => ({
  id: `enf_audit_${beforeTerminalId}_${index}`,
  type: 'policy_audit',
  label: 'Compliance Audit',
  config: {
    auditScope: 'workflow-execution',
    generateCertificate: true,
    hashChainVerification: true,
    includeDataLineage: true,
    description: 'Auto-inserted: generates tamper-evident audit trail with hash chain verification',
  },
  _enforcement: true,
  _insertedBefore: beforeTerminalId,
  _autoInserted: true,
});

const createAutonomyGateNode = (beforeActionId, index) => ({
  id: `enf_autonomy_${beforeActionId}_${index}`,
  type: 'agent_autonomy_gate',
  label: 'Autonomy Gate',
  config: {
    minimumLevel: 'SUPERVISED',
    checkComplianceScore: true,
    checkProbationStatus: true,
    fallbackAction: 'REQUIRE_APPROVAL',
    description: 'Auto-inserted: verifies agent trust level before executing autonomous actions',
  },
  _enforcement: true,
  _insertedBefore: beforeActionId,
  _autoInserted: true,
});

const createDpgInternodeNode = (sourceNodeId, targetNodeId, index) => ({
  id: `enf_dpg_internode_${sourceNodeId}_${targetNodeId}_${index}`,
  type: 'dpg_internode_scan',
  label: 'DPG Inter-Node Scan',
  config: {
    autoInserted: true,
    enforcementType: 'DPG_INTERNODE',
    sourceNodeId,
    targetNodeId,
    scanMode: 'CLASSIFICATION_ONLY',
    trackLineage: true,
    description: 'Auto-inserted: lightweight classification scan tracking data flow between nodes without tokenization overhead',
  },
  _enforcement: true,
  _insertedBetween: { sourceNodeId, targetNodeId },
  _autoInserted: true,
});

// ─── Position calculation ─────────────────────────────────────────────

const X_OFFSET_ENFORCEMENT = 100; // horizontal spacing between inserted nodes
const Y_OFFSET_ENFORCEMENT = 80;  // vertical offset for enforcement nodes (below main flow)

/**
 * Calculate position for an enforcement node inserted between two nodes.
 */
function calcInsertPosition(fromNode, toNode) {
  const midX = (fromNode.x + toNode.x) / 2;
  const midY = (fromNode.y + toNode.y) / 2;
  return { x: midX, y: midY + Y_OFFSET_ENFORCEMENT };
}

// ─── Core insertion engine ────────────────────────────────────────────

/**
 * Build lookup maps for fast graph traversal.
 */
function buildGraphMaps(nodes, connections) {
  const nodeMap = new Map();
  nodes.forEach(n => nodeMap.set(n.id, n));

  // outgoing[nodeId] = [{ connection, targetNodeId }]
  const outgoing = new Map();
  // incoming[nodeId] = [{ connection, sourceNodeId }]
  const incoming = new Map();

  connections.forEach(c => {
    if (!outgoing.has(c.from)) outgoing.set(c.from, []);
    outgoing.get(c.from).push({ connection: c, targetNodeId: c.to });

    if (!incoming.has(c.to)) incoming.set(c.to, []);
    incoming.get(c.to).push({ connection: c, sourceNodeId: c.from });
  });

  return { nodeMap, outgoing, incoming };
}

/**
 * Insert an enforcement node between two connected nodes.
 * Returns the new node and updated connections.
 */
function spliceEnforcementNode(enfNode, originalConn, fromNode, toNode) {
  const pos = calcInsertPosition(fromNode, toNode);
  enfNode.x = pos.x;
  enfNode.y = pos.y;

  // Remove original connection, add two new ones
  const connToEnf = {
    id: `conn_${originalConn.from}_to_${enfNode.id}`,
    from: originalConn.from,
    fromPort: originalConn.fromPort,
    to: enfNode.id,
    toPort: enforcementInPort(enfNode.type),
  };
  const connFromEnf = {
    id: `conn_${enfNode.id}_to_${originalConn.to}`,
    from: enfNode.id,
    fromPort: enforcementOutPort(enfNode.type),
    to: originalConn.to,
    toPort: originalConn.toPort,
  };

  return { node: enfNode, removeConn: originalConn.id, addConns: [connToEnf, connFromEnf] };
}

/**
 * Insert multiple enforcement nodes in sequence between two connected nodes.
 * For example: [DPG_INBOUND, TOKEN_VAULT] between connector and next node.
 */
function spliceEnforcementChain(enfNodes, originalConn, fromNode, toNode) {
  if (enfNodes.length === 0) return { nodes: [], removeConn: null, addConns: [] };

  const results = { nodes: [], removeConn: originalConn.id, addConns: [] };

  // Calculate evenly-spaced positions
  const dx = (toNode.x - fromNode.x) / (enfNodes.length + 1);
  const midY = (fromNode.y + toNode.y) / 2;

  let prevId = originalConn.from;
  let prevPort = originalConn.fromPort;

  enfNodes.forEach((enfNode, i) => {
    enfNode.x = fromNode.x + dx * (i + 1);
    enfNode.y = midY + Y_OFFSET_ENFORCEMENT;

    results.addConns.push({
      id: `conn_${prevId}_to_${enfNode.id}`,
      from: prevId,
      fromPort: prevPort,
      to: enfNode.id,
      toPort: enforcementInPort(enfNode.type),
    });

    results.nodes.push(enfNode);
    prevId = enfNode.id;
    prevPort = enforcementOutPort(enfNode.type);
  });

  // Final connection to original target — leaves the LAST enforcement node in
  // the chain, so the port is that node's, not a literal.
  results.addConns.push({
    id: `conn_${prevId}_to_${originalConn.to}`,
    from: prevId,
    fromPort: prevPort,
    to: originalConn.to,
    toPort: originalConn.toPort,
  });

  return results;
}

// ─── Main export: autoInsertEnforcementNodes ──────────────────────────

/**
 * Analyze a playbook template and auto-insert enforcement nodes.
 *
 * @param {Object} playbook - { nodes: [], connections: [] }
 * @returns {Object} - { nodes: [], connections: [], insertionReport: [] }
 *
 * The insertionReport describes every enforcement node added, enabling
 * the UI to animate them appearing on the canvas.
 */
export function autoInsertEnforcementNodes(playbook) {
  const originalNodes = [...playbook.nodes];
  const originalConnections = [...playbook.connections];

  // Skip if the playbook already has platform enforcement nodes (auto-inserted DPG, policy gates, etc.)
  // Do NOT treat user-placed workflow nodes (e.g. approval_gate) as enforcement —
  // they are workflow logic, not platform governance.
  const hasEnforcement = originalNodes.some(n =>
    n._autoInserted === true || PLATFORM_ENFORCEMENT_TYPES.has(n.type)
  );
  if (hasEnforcement) {
    return {
      nodes: originalNodes,
      connections: originalConnections,
      insertionReport: [],
      alreadyGoverned: true,
    };
  }

  const { nodeMap, outgoing, incoming } = buildGraphMaps(originalNodes, originalConnections);

  const newNodes = [];
  const removeConnIds = new Set();
  const addConns = [];
  const insertionReport = [];
  let enfIndex = 0;

  // Track which action nodes have already received an autonomy gate
  const autonomyGatedActions = new Set();
  let firstActionGated = false;

  // ── Rule 1: DPG_INBOUND after connector nodes ──
  for (const node of originalNodes) {
    if (!CONNECTOR_TYPES.has(node.type)) continue;
    // Skip trigger nodes (they start the flow, not fetch data)
    if (node.type === 'siem_alert' || node.type === 'email_trigger') continue;

    const edges = outgoing.get(node.id) || [];
    for (const edge of edges) {
      const targetNode = nodeMap.get(edge.targetNodeId);
      if (!targetNode || ENFORCEMENT_TYPES.has(targetNode.type)) continue;

      const dpgNode = createDpgInboundNode(node.id, enfIndex);
      const vaultNode = createTokenVaultNode(dpgNode.id, enfIndex);
      enfIndex++;

      const result = spliceEnforcementChain(
        [dpgNode, vaultNode],
        edge.connection,
        node,
        targetNode,
      );

      newNodes.push(...result.nodes);
      removeConnIds.add(result.removeConn);
      addConns.push(...result.addConns);

      insertionReport.push({
        type: 'DPG_INBOUND_CHAIN',
        enforcementNodes: [dpgNode.id, vaultNode.id],
        afterNode: node.id,
        beforeNode: targetNode.id,
        reason: `Data from ${node.label || node.type} is scanned for PII and sensitive values are tokenized before processing`,
      });
    }
  }

  // Rebuild graph with inserted nodes for subsequent rules
  const allNodesPhase1 = [...originalNodes, ...newNodes];
  const allConnsPhase1 = [
    ...originalConnections.filter(c => !removeConnIds.has(c.id)),
    ...addConns,
  ];
  const maps2 = buildGraphMaps(allNodesPhase1, allConnsPhase1);

  // ── Rule 2: DPG_OUTBOUND before LLM nodes ──
  const newNodesPhase2 = [];
  const removeConnIds2 = new Set();
  const addConns2 = [];

  for (const node of allNodesPhase1) {
    if (!LLM_TYPES.has(node.type)) continue;

    const edges = maps2.incoming.get(node.id) || [];
    for (const edge of edges) {
      const sourceNode = maps2.nodeMap.get(edge.sourceNodeId);
      if (!sourceNode || ENFORCEMENT_TYPES.has(sourceNode.type)) continue;

      const dpgOut = createDpgOutboundNode(node.id, enfIndex);
      enfIndex++;

      const result = spliceEnforcementChain(
        [dpgOut],
        edge.connection,
        sourceNode,
        node,
      );

      newNodesPhase2.push(...result.nodes);
      removeConnIds2.add(result.removeConn);
      addConns2.push(...result.addConns);

      insertionReport.push({
        type: 'DPG_OUTBOUND',
        enforcementNodes: [dpgOut.id],
        afterNode: sourceNode.id,
        beforeNode: node.id,
        reason: `Data is scanned for PII before being sent to ${node.label || node.type} to prevent sensitive data leakage to LLM`,
      });
    }
  }

  // Phase 2 rebuild
  const allNodesPhase2 = [...allNodesPhase1, ...newNodesPhase2];
  const allConnsPhase2 = [
    ...allConnsPhase1.filter(c => !removeConnIds2.has(c.id)),
    ...addConns2,
  ];
  const maps3 = buildGraphMaps(allNodesPhase2, allConnsPhase2);

  // ── Rule 3: POLICY_GATE + AUTONOMY_GATE before action nodes ──
  const newNodesPhase3 = [];
  const removeConnIds3 = new Set();
  const addConns3 = [];

  for (const node of allNodesPhase2) {
    if (!ACTION_TYPES.has(node.type)) continue;

    const edges = maps3.incoming.get(node.id) || [];
    for (const edge of edges) {
      const sourceNode = maps3.nodeMap.get(edge.sourceNodeId);
      if (!sourceNode || ENFORCEMENT_TYPES.has(sourceNode.type)) continue;

      const enforcementChain = [];

      // Add autonomy gate only for the first action node
      if (!firstActionGated) {
        const autonomyNode = createAutonomyGateNode(node.id, enfIndex);
        enforcementChain.push(autonomyNode);
        firstActionGated = true;

        insertionReport.push({
          type: 'AUTONOMY_GATE',
          enforcementNodes: [autonomyNode.id],
          beforeNode: node.id,
          reason: `Agent trust level is verified before executing autonomous action: ${node.label || node.type}`,
        });
      }

      const policyNode = createPolicyGateNode(node.id, enfIndex);
      enforcementChain.push(policyNode);
      enfIndex++;

      const result = spliceEnforcementChain(
        enforcementChain,
        edge.connection,
        sourceNode,
        node,
      );

      newNodesPhase3.push(...result.nodes);
      removeConnIds3.add(result.removeConn);
      addConns3.push(...result.addConns);

      insertionReport.push({
        type: 'POLICY_GATE',
        enforcementNodes: [policyNode.id],
        beforeNode: node.id,
        reason: `Policy rules are enforced before executing: ${node.label || node.type}`,
      });
    }
  }

  // Phase 3 rebuild
  const allNodesPhase3 = [...allNodesPhase2, ...newNodesPhase3];
  const allConnsPhase3 = [
    ...allConnsPhase2.filter(c => !removeConnIds3.has(c.id)),
    ...addConns3,
  ];
  const maps4 = buildGraphMaps(allNodesPhase3, allConnsPhase3);

  // ── Rule 4: AUDIT node before terminal nodes ──
  const newNodesPhase4 = [];
  const removeConnIds4 = new Set();
  const addConns4 = [];

  for (const node of allNodesPhase3) {
    const isTerminal = TERMINAL_TYPES.has(node.type) ||
      (node.config && node.config.action === 'complete-workflow') ||
      (node.config && node.config.action === 'log-and-flag');

    if (!isTerminal) continue;

    const edges = maps4.incoming.get(node.id) || [];
    for (const edge of edges) {
      const sourceNode = maps4.nodeMap.get(edge.sourceNodeId);
      if (!sourceNode || ENFORCEMENT_TYPES.has(sourceNode.type)) continue;

      const auditNode = createAuditNode(node.id, enfIndex);
      enfIndex++;

      const result = spliceEnforcementChain(
        [auditNode],
        edge.connection,
        sourceNode,
        node,
      );

      newNodesPhase4.push(...result.nodes);
      removeConnIds4.add(result.removeConn);
      addConns4.push(...result.addConns);

      insertionReport.push({
        type: 'COMPLIANCE_AUDIT',
        enforcementNodes: [auditNode.id],
        beforeNode: node.id,
        reason: 'Tamper-evident compliance audit trail with hash chain verification is generated before workflow completion',
      });
    }
  }

  // Phase 4 rebuild
  const allNodesPhase4 = [...allNodesPhase3, ...newNodesPhase4];
  const allConnsPhase4 = [
    ...allConnsPhase3.filter(c => !removeConnIds4.has(c.id)),
    ...addConns4,
  ];
  const maps5 = buildGraphMaps(allNodesPhase4, allConnsPhase4);

  // ── Phase 5: DPG_INTERNODE between every pair of connected nodes ──
  const newNodesPhase5 = [];
  const removeConnIds5 = new Set();
  const addConns5 = [];

  // Collect all connections in current graph
  for (const conn of allConnsPhase4) {
    const sourceNode = maps5.nodeMap.get(conn.from);
    const targetNode = maps5.nodeMap.get(conn.to);

    // Skip if either node doesn't exist
    if (!sourceNode || !targetNode) continue;

    // Skip connections where BOTH source AND target are enforcement nodes
    const sourceIsEnforcement = ENFORCEMENT_TYPES.has(sourceNode.type);
    const targetIsEnforcement = ENFORCEMENT_TYPES.has(targetNode.type);
    if (sourceIsEnforcement && targetIsEnforcement) continue;

    // Skip connections where source or target is already a DPG node of any type
    if ((sourceNode.type && sourceNode.type.startsWith('dpg')) ||
        (targetNode.type && targetNode.type.startsWith('dpg'))) continue;

    // G459 — Skip connections originating from a flow sentinel (terminate /
    // end). These node types are workflow flow sentinels: their outbound
    // edges are structurally invalid per the workflow contract (terminate
    // means "stop the flow"). Even if a malformed payload contains such an
    // edge (e.g., a buggy authoring tool or Conductor round-trip emitted
    // `terminate → next_node`), we refuse to splice an enforcement scan in
    // — that would compound the structural violation by injecting a node
    // into a flow that should have already ended.
    //
    // Without this guard, the injector produced chains like
    // `terminate → dpg_internode_scan → next_node`, and the IR walker
    // correctly refused to traverse past the terminate, leaving the scan
    // and any downstream nodes as orphans (W133 surfaced them as
    // Disconnected on Native Insurance Claim v37).
    //
    // We also skip when the TARGET is a flow sentinel; inserting a scan
    // immediately before a terminate is benign but pointless (the
    // terminate halts execution, so the scan's outputs are unused) and
    // is also the symptom of a malformed `→ terminate ← terminate`
    // pattern that should be cleaned up at the source.
    if (sourceNode.type && FLOW_SENTINEL_TYPES.has(sourceNode.type)) continue;
    if (targetNode.type && FLOW_SENTINEL_TYPES.has(targetNode.type)) continue;

    // Insert DPG_INTERNODE between source and target
    const internodeNode = createDpgInternodeNode(conn.from, conn.to, enfIndex);
    enfIndex++;

    const result = spliceEnforcementNode(internodeNode, conn, sourceNode, targetNode);

    newNodesPhase5.push(result.node);
    removeConnIds5.add(result.removeConn);
    addConns5.push(...result.addConns);

    insertionReport.push({
      type: 'DPG_INTERNODE',
      enforcementNodes: [internodeNode.id],
      sourceNode: conn.from,
      targetNode: conn.to,
      reason: `Lightweight classification scan tracking zero-trust data flow from ${sourceNode.label || sourceNode.type} to ${targetNode.label || targetNode.type}`,
    });
  }

  // ── Final assembly ──
  const finalNodes = [...allNodesPhase4, ...newNodesPhase5];
  const finalConnections = [
    ...allConnsPhase4.filter(c => !removeConnIds5.has(c.id)),
    ...addConns5,
  ];

  // RC-CanvasV3-ProtectManifest — compute the manifest by diffing
  // ORIGINAL (pre-protect) state against FINAL (post-insert) state.
  // The manifest is what Unprotect replays in reverse: exactly the
  // nodes Protect added, exactly the edges Protect removed (so we
  // can restore them), exactly the edges Protect added (so we can
  // remove them).
  //
  // Edge identity is structural (from/to/fromPort/toPort) because
  // connection `id` is not stable across save/reload — JsonNode
  // serialization may strip or rewrite ids depending on the
  // canvas helper used. The structural key is durable through the
  // BE round-trip.
  const protectManifest = computeProtectManifest(
    { nodes: originalNodes, connections: originalConnections },
    { nodes: finalNodes,    connections: finalConnections }
  );

  return {
    nodes: finalNodes,
    connections: finalConnections,
    protectManifest,
    insertionReport,
    alreadyGoverned: false,
    stats: {
      originalNodeCount: originalNodes.length,
      enforcementNodesAdded: finalNodes.length - originalNodes.length,
      totalNodeCount: finalNodes.length,
      dpgInboundCount: insertionReport.filter(r => r.type === 'DPG_INBOUND_CHAIN').length,
      dpgOutboundCount: insertionReport.filter(r => r.type === 'DPG_OUTBOUND').length,
      policyGateCount: insertionReport.filter(r => r.type === 'POLICY_GATE').length,
      autonomyGateCount: insertionReport.filter(r => r.type === 'AUTONOMY_GATE').length,
      auditCount: insertionReport.filter(r => r.type === 'COMPLIANCE_AUDIT').length,
      dpgInternodeCount: insertionReport.filter(r => r.type === 'DPG_INTERNODE').length,
    },
  };
}

/**
 * Utility: Check if a node was auto-inserted by the enforcement engine.
 */
export function isAutoInsertedNode(node) {
  return node && node._autoInserted === true;
}

/**
 * Check if a node is a platform enforcement node by type.
 * Does NOT match general-purpose nodes like approval_gate or data_classification
 * even if they appear in the broader ENFORCEMENT_TYPES set.
 */
export function isPlatformEnforcementNode(node) {
  return node && PLATFORM_ENFORCEMENT_TYPES.has(node.type);
}

/**
 * Utility: Get all enforcement node IDs from a template result.
 */
export function getEnforcementNodeIds(result) {
  return result.insertionReport.flatMap(r => r.enforcementNodes || []);
}

/**
 * Inverse of {@link autoInsertEnforcementNodes}: strip every auto-inserted
 * or platform-enforcement node from a canvas and re-stitch the surrounding
 * edges so the original (un-protected) graph topology is restored.
 *
 * <p>Used by the Canvas toolbar's Protect button when toggling protection
 * OFF. Workflow author authored a clean DAG (A → B → C); we'd inserted
 * A → DPG_in → B → DPG_internode → C → DPG_out; we now want A → B → C
 * back without touching the operator's own logic nodes.</p>
 *
 * <h3>Edge re-stitching</h3>
 * For each enforcement node E with upstream edges {A, A'} → E and downstream
 * edges E → {B, B'}, we drop the four edges that touch E and add A → B,
 * A → B', A' → B, A' → B' (cartesian re-connect). For 1-in / 1-out
 * (the overwhelmingly common case for auto-inserted enforcement) this
 * reduces to A → B. Duplicate edges are deduped by (source, target)
 * pair so a node already connected to a downstream stays connected
 * once.
 *
 * <h3>What counts as enforcement</h3>
 * The same predicates the Protect button uses to detect "is the
 * workflow currently protected": {@link isAutoInsertedNode} (the engine's
 * own flag) OR {@link isPlatformEnforcementNode} (type-based — DPG scans,
 * token vault, policy gates). User-authored {@code approval_gate} or
 * {@code data_classification} nodes are NOT considered platform
 * enforcement and are left in place.
 */
/**
 * RC-CanvasV3-ProtectManifest — structural key for an edge.
 *
 * Used to compare edges across the protect → save → reload → unprotect
 * round-trip where connection `id` may be rewritten by the canvas
 * helper (autosave / version-restore / IR-roundtrip all mint fresh
 * ids in places). Tuple of (from, to, fromPort, toPort) is durable
 * because those are operator-meaningful and never rewritten silently.
 */
function edgeKey(c) {
  if (!c) return '';
  const from = c.from ?? c.source ?? '';
  const to   = c.to   ?? c.target ?? '';
  const fp   = c.fromPort || c.sourceHandle || 'default';
  const tp   = c.toPort   || c.targetHandle || 'default';
  return `${from}|${fp}|${to}|${tp}`;
}

/**
 * RC-CanvasV3-ProtectManifest — diff original vs. final canvas state
 * and emit a manifest that captures EXACTLY what Protect changed.
 *
 * Manifest schema (durable across BE round-trip):
 * <pre>{
 *   protectVersion:      1,
 *   protectedAt:         <epoch ms>,
 *   insertedNodeIds:     ['enf_dpg_in_X', ...],
 *   addedConnections:    [{from, to, fromPort, toPort}, ...],
 *   removedConnections:  [{from, to, fromPort, toPort}, ...],
 * }</pre>
 *
 * Unprotect replays this in reverse — no inference, no bypass logic,
 * no fork pathology. The current ProtectVersion is 1; future schema
 * changes bump the version and the replay function dispatches per
 * version so existing manifests keep working.
 */
function computeProtectManifest(before, after) {
  const beforeNodeIds = new Set((before.nodes || []).map((n) => n && n.id).filter(Boolean));
  const afterNodes = after.nodes || [];
  const insertedNodeIds = afterNodes
    .map((n) => n && n.id)
    .filter((id) => id && !beforeNodeIds.has(id));

  const beforeEdgeKeys = new Set((before.connections || []).map(edgeKey));
  const afterEdgeKeys  = new Set((after.connections  || []).map(edgeKey));
  const toEdgeRecord = (c) => ({
    from:     c.from ?? c.source,
    to:       c.to   ?? c.target,
    fromPort: c.fromPort || c.sourceHandle || 'default',
    toPort:   c.toPort   || c.targetHandle || 'default',
  });
  const addedConnections   = (after.connections  || [])
    .filter((c) => !beforeEdgeKeys.has(edgeKey(c)))
    .map(toEdgeRecord);
  const removedConnections = (before.connections || [])
    .filter((c) => !afterEdgeKeys.has(edgeKey(c)))
    .map(toEdgeRecord);

  return {
    protectVersion:     1,
    protectedAt:        Date.now(),
    insertedNodeIds,
    addedConnections,
    removedConnections,
  };
}

/**
 * RC-CanvasV3-ProtectManifest — exact-replay Unprotect.
 *
 * The ONLY work this does is reverse the manifest's recorded operations:
 *   1. Remove exactly the nodes Protect inserted (by id match).
 *   2. Remove exactly the edges Protect added (by structural key match).
 *   3. Restore exactly the edges Protect removed (deduped against
 *      surviving edges so a user-added duplicate isn't doubled).
 *
 * Robustness: after the replay, filter the connection list to only
 * edges whose endpoints still exist in the surviving node set. This
 * handles the rare case where the operator deleted one of their own
 * nodes between Protect and Unprotect — the restored edge would
 * otherwise reference a non-existent endpoint and trip
 * validateAndRepairDAG.
 *
 * Returns the canonical shape (matches the legacy stripEnforcementNodes
 * contract: { nodes, connections, removedNodeCount, removedNodes }),
 * minus the manifest field which is cleared so a second Unprotect
 * is a no-op rather than re-running.
 */
function exactReplayUnprotect(canvasData, manifest) {
  const nodes       = Array.isArray(canvasData?.nodes)       ? canvasData.nodes       : [];
  const connections = Array.isArray(canvasData?.connections) ? canvasData.connections : [];

  const insertedSet = new Set(manifest.insertedNodeIds || []);
  const addedSet    = new Set((manifest.addedConnections || []).map(edgeKey));
  const removedList = manifest.removedConnections || [];

  // 1. Remove the inserted nodes.
  const survivingNodes = nodes
    .filter((n) => n && !insertedSet.has(n.id))
    .map((n) => ({ ...n }));
  const survivingNodeIds = new Set(survivingNodes.map((n) => n.id));

  // 2. Drop the edges Protect added; also drop any edge whose endpoint
  //    no longer exists (e.g. user deleted a node mid-cycle, or one
  //    end was an inserted node we just removed).
  let survivingConnections = connections
    .filter((c) => {
      if (addedSet.has(edgeKey(c))) return false;
      const src = c.from ?? c.source;
      const tgt = c.to   ?? c.target;
      return survivingNodeIds.has(src) && survivingNodeIds.has(tgt);
    })
    .map((c) => ({ ...c }));

  // 3. Restore Protect-removed edges (skip duplicates and skip edges
  //    whose endpoints no longer exist after step 1).
  const haveKeys = new Set(survivingConnections.map(edgeKey));
  for (const r of removedList) {
    const key = edgeKey(r);
    if (haveKeys.has(key)) continue;
    if (!survivingNodeIds.has(r.from) || !survivingNodeIds.has(r.to)) continue;
    survivingConnections.push({
      from: r.from, to: r.to, fromPort: r.fromPort, toPort: r.toPort,
    });
    haveKeys.add(key);
  }

  return {
    nodes: survivingNodes,
    connections: survivingConnections,
    removedNodeCount: insertedSet.size,
    removedNodes: Array.from(insertedSet),
    // Manifest cleared — Unprotect is now a no-op until next Protect
    // attaches a new one. Caller (handleProtectWorkflow) should drop
    // the manifest from the canvas data it persists.
    protectManifestCleared: true,
  };
}

export function stripEnforcementNodes(canvasData) {
  const nodes = Array.isArray(canvasData?.nodes) ? canvasData.nodes : [];
  const connections = Array.isArray(canvasData?.connections) ? canvasData.connections : [];

  // RC-CanvasV3-Protect-Strip-Authorship — strip decides what to REMOVE,
  // which is a question about AUTHORSHIP, not node-type. The previous
  // predicate also included `isPlatformEnforcementNode(n)` (type-based),
  // which nuked any user-authored node whose type happened to be in the
  // governance family (`policy_audit`, `dpg_internode_scan`, etc.) — e.g.
  // the HelloWorld template seeds those as user-authored steps. Repeated
  // Protect/Unprotect cycles wiped them and the inner edges between
  // them (bypass logic can't bridge enf→enf adjacencies; both endpoints
  // get filtered out, no replacement edge is created), leaving Hello
  // World orphaned and the canvas in tatters.
  //
  // Authorship has three explicit signals; strip uses ONLY these:
  //   1. `_autoInserted: true` — the FE engine's own flag, set on every
  //      template in this file (DPG inbound/outbound/internode, policy
  //      gate, audit recorder, token vault, classification, scoring).
  //   2. `id` starts with `enf_` — the prefix every auto-insert template
  //      uses. Belt-and-suspenders backstop for the rare case where
  //      `_autoInserted` is dropped during a BE round-trip (the column
  //      is JSONB and underscore-prefixed keys *should* survive, but the
  //      id prefix is durable regardless).
  //   3. `config.gelPhase` set and not in PRESERVED_GEL_PHASES — the
  //      backend's GEL scaffolding (Pre-Gate / Enforcement Decision /
  //      audit_record / Block Notification / Compliance Record /
  //      Execution Complete). The actual external-execution node
  //      (`gelPhase: EXECUTE_EXTERNAL`) and inline external tasks
  //      (`INLINE_EXTERNAL_TASK`) are the workflow's purpose and stay.
  //
  // NOTE: `isPlatformEnforcementNode(n)` (type-based) is RETAINED for
  // `isWorkflowProtected()` at the call site — there the question is "is
  // anything governance-shaped on the canvas?" which is type-based by
  // design. Strip and detect are different questions; the previous code
  // conflated them.
  const PRESERVED_GEL_PHASES = new Set(['EXECUTE_EXTERNAL', 'INLINE_EXTERNAL_TASK']);
  const isGelEnforcement = (n) => {
    const phase = n && n.config && typeof n.config.gelPhase === 'string'
      ? n.config.gelPhase
      : null;
    return phase != null && !PRESERVED_GEL_PHASES.has(phase);
  };
  const hasAutoInsertedIdPrefix = (n) =>
    n && typeof n.id === 'string' && n.id.startsWith('enf_');

  const enforcementIds = new Set(
    nodes
      .filter((n) => isAutoInsertedNode(n) || hasAutoInsertedIdPrefix(n) || isGelEnforcement(n))
      .map((n) => n.id)
  );

  if (enforcementIds.size === 0) {
    return {
      nodes: nodes.map((n) => ({ ...n })),
      connections: connections.map((c) => ({ ...c })),
      removedNodeCount: 0,
      removedNodes: [],
    };
  }

  // RC-CanvasV3-ProtectManifest — manifest-based exact replay path.
  //
  // The previous bypass-inference designs (single-node and multi-node BFS)
  // tried to RECONSTRUCT the pre-Protect topology by inferring edges
  // around the removed enforcement nodes. That approach is structurally
  // wrong — every inference layer introduced new pathologies (orphan
  // forks, cycles, port-loss, switch-cases drift). The right design
  // is: Protect records exactly what it did, Unprotect replays the
  // inverse. No inference, no traversal, no fork.
  //
  // The manifest is attached to the canvas data by autoInsertEnforcement
  // Nodes() at protect time as `canvasData.protectManifest`:
  //   {
  //     protectVersion: 1,
  //     insertedNodeIds: ['enf_dpg_in_n_hw_0', ...],
  //     addedConnections: [{from, to, fromPort, toPort}, ...],
  //     removedConnections: [{from, to, fromPort, toPort}, ...],
  //   }
  // When present, exact replay is the only thing we do — bypass logic
  // is dead code. When absent (workflows protected before this change,
  // or hand-authored governance topologies), we fall back to a
  // conservative removal that touches ONLY auto-inserted nodes and
  // leaves topology repair to validateAndRepairDAG.
  const manifest = canvasData?.protectManifest;
  if (manifest && Array.isArray(manifest.insertedNodeIds)) {
    return exactReplayUnprotect(canvasData, manifest);
  }

  // ── Manifest-absent fallback ──────────────────────────────────────
  // Pre-manifest workflows or hand-authored governance. Remove the
  // enforcement nodes by authorship signal (the RC-231 predicate) but
  // do NOT synthesise any bypass edges — keeps the data plane truthful.
  // Leave orphan reconnection to validateAndRepairDAG, which has its
  // own (separate) test coverage. This is strictly less destructive
  // than the old bypass-inference code: every edge that survives is
  // an edge the operator actually authored.
  //
  // The downstream switch/cases-cleanup block below still runs to
  // re-point config.cases references that pointed at stripped nodes;
  // it consumes downstreamByEnf, so build that here for backward
  // compatibility with the existing block.
  const survivingConnections = connections.filter((c) => {
    const src = c.from ?? c.source;
    const tgt = c.to ?? c.target;
    return !enforcementIds.has(src) && !enforcementIds.has(tgt);
  }).map((c) => ({ ...c }));

  const downstreamByEnf = new Map();
  connections.forEach((c) => {
    const src = c.from ?? c.source;
    const tgt = c.to ?? c.target;
    if (!src || !tgt) return;
    if (enforcementIds.has(src) && !enforcementIds.has(tgt)) {
      if (!downstreamByEnf.has(src)) downstreamByEnf.set(src, []);
      downstreamByEnf.get(src).push({ target: tgt, toPort: c.toPort, targetHandle: c.targetHandle });
    }
  });

  const survivingNodes = nodes
    .filter((n) => !enforcementIds.has(n.id))
    .map((n) => ({ ...n }));

  // ── Clean up orphan references in surviving switch/condition nodes ─────
  // GEL materialization seeds `config.cases.<port> = [<targetNodeId>]` on
  // condition/switch nodes (e.g. Enforcement Decision routes
  // cases.ALLOW = [executeId], cases.BLOCK = [blockRecId]). After strip,
  // those arrays may reference deleted enforcement nodes. v2Lint flags any
  // such dangling reference with an ERR badge ("ERR · 2" on the surviving
  // switch). Re-point each deleted reference to the bypass target derived
  // from the strip's downstream map; if nothing maps, drop the entry.
  // Same treatment for the legacy `config.routes` map used by the AI
  // generator path.
  const stripFromCaseArray = (arr, fallbackTargets) => {
    if (!Array.isArray(arr)) return arr;
    const cleaned = [];
    for (const target of arr) {
      if (typeof target === 'string' && enforcementIds.has(target)) {
        // Replace each deleted reference with the bypass target. Pull
        // downstream targets from our earlier map so we know where the
        // workflow now flows after the deleted node was bypassed.
        const downs = downstreamByEnf.get(target) || [];
        downs.forEach((d) => { if (!cleaned.includes(d.target)) cleaned.push(d.target); });
      } else if (typeof target === 'string') {
        cleaned.push(target);
      }
    }
    return cleaned;
  };

  for (const n of survivingNodes) {
    const cfg = n && n.config;
    if (!cfg || typeof cfg !== 'object') continue;

    if (cfg.cases && typeof cfg.cases === 'object') {
      const newCases = {};
      for (const [portName, value] of Object.entries(cfg.cases)) {
        if (Array.isArray(value)) {
          const cleaned = stripFromCaseArray(value);
          if (cleaned.length > 0) newCases[portName] = cleaned;
        } else if (typeof value === 'string') {
          if (!enforcementIds.has(value)) {
            newCases[portName] = value;
          } else {
            const downs = downstreamByEnf.get(value) || [];
            if (downs.length > 0) newCases[portName] = downs[0].target;
          }
        } else {
          newCases[portName] = value;
        }
      }
      cfg.cases = newCases;
    }

    if (cfg.routes && typeof cfg.routes === 'object') {
      const newRoutes = {};
      for (const [portName, target] of Object.entries(cfg.routes)) {
        if (typeof target === 'string' && enforcementIds.has(target)) {
          const downs = downstreamByEnf.get(target) || [];
          if (downs.length > 0) newRoutes[portName] = downs[0].target;
        } else {
          newRoutes[portName] = target;
        }
      }
      cfg.routes = newRoutes;
    }
  }

  return {
    nodes: survivingNodes,
    connections: survivingConnections,
    removedNodeCount: enforcementIds.size,
    removedNodes: Array.from(enforcementIds),
  };
}
